import { Code, ConnectError } from '@bufbuild/connect';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';

import api from '@/shared/api/api';
import { OrganizationStaff } from '@/shared/api/protocol-ts/model/dto_organization_staff_pb';
import { PATHS } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { debug, isConnectNetworkError } from '@/shared/lib';

import { organizationModel } from '@/entities/organization';
import { organizationStaffModel } from '@/entities/organizationStaff';
import { userModel } from '@/entities/user';

export const useOrganizationSettingsStream = () => {
  const organizationID = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );
  const currentUserID = useAppSelector(userModel.selectors.selectCurrentUserID);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const abortControllerRef = useRef<AbortController | null>(null);

  const openOrganizationSettingsStream = async () => {
    dispatch(organizationModel.actions.setLoading('pending'));

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const staffAccumulator: OrganizationStaff[] = [];

    try {
      const organizationSettingsStream =
        api.organization.organizationSettingsStream(
          { OrganizationID: organizationID },
          { signal: controller.signal },
        );

      for await (const { Update } of organizationSettingsStream) {
        switch (Update.case) {
          case 'HistoricalOrganization': {
            dispatch(organizationModel.actions.setOne(Update.value));
            // MIRender observes setNewestOne, not setOne — deliver FDA flags on initial load.
            dispatch(organizationModel.actions.setNewestOne(Update.value));
            dispatch(
              organizationModel.actions.setSettingsLoadedForOrganizationID(
                Update.value.ID,
              ),
            );
            break;
          }

          case 'UpdatedOrganization': {
            dispatch(organizationModel.actions.setNewestOne(Update.value));
            break;
          }

          case 'HistoricalOrganizationStaff': {
            staffAccumulator.push(Update.value);
            break;
          }

          case 'EndOfHistoricalOrganizationStaff': {
            // Replace the entire snapshot so staff deleted while offline are removed
            dispatch(
              organizationStaffModel.actions.removeByOrganizationID(
                organizationID,
              ),
            );
            dispatch(organizationStaffModel.actions.setMany(staffAccumulator));
            staffAccumulator.length = 0;
            dispatch(organizationModel.actions.setLoading('succeeded'));
            break;
          }

          case 'UpdatedOrganizationStaff': {
            // Losing our own membership drops the organization;
            // useSyncOrganizationID then switches to another one.
            if (
              Update.value.UserID === currentUserID &&
              Update.value.Status?.case === 'Deleted'
            ) {
              dispatch(organizationModel.actions.removeOne(organizationID));
              break;
            }

            dispatch(organizationStaffModel.actions.setNewestOne(Update.value));
            break;
          }

          case 'HistoricalOrganizationStorageSize':
          case 'UpdatedOrganizationStorageSize': {
            dispatch(
              organizationModel.actions.setStorageUsed(
                Update.value.Total.toString(),
              ),
            );
            break;
          }

          // Parent DSO is context-only: storing it would pollute the org switcher.
          case 'HistoricalParentDSOOrganization':
          case 'UpdatedParentDSOOrganization': {
            break;
          }
        }
      }
    } catch (error) {
      const connectError = ConnectError.from(error);

      // Intentional abort (unmount, org switch) — not an error.
      if (connectError.code === Code.Canceled) {
        dispatch(organizationModel.actions.setLoading('succeeded'));
        return;
      }

      if (isConnectNetworkError(connectError)) {
        debug('Network error detected, attempting to restart stream...');
        closeOrganizationSettingsStream();
        openOrganizationSettingsStream();
      } else {
        dispatch(organizationModel.actions.setLoading('succeeded'));
      }

      if (connectError.code === Code.Unauthenticated) {
        navigate(PATHS.signIn, {
          state: { from: location?.pathname + location?.search },
        });
      }

      if (connectError.code === Code.NotFound) {
        navigate(PATHS.patients);
      }

      console.error(
        'OrganizationSettingsStream: error: ',
        connectError.message,
        connectError.code,
        connectError.rawMessage,
        connectError,
      );
    }
  };

  const closeOrganizationSettingsStream = () => {
    abortControllerRef.current?.abort();
  };

  useEffect(() => {
    if (organizationID) {
      openOrganizationSettingsStream();
    }

    return () => {
      closeOrganizationSettingsStream();
    };
  }, [organizationID]);
};
